import type { UseQueryOptions } from '@tanstack/react-query';
import { NoddiAsyncError } from 'noddi-types';
import { mergeSideEffectGetQueryConfig } from './mergeSideEffectGetQueryConfig';
import { getUrls, type URLKeysTypes } from './resources/resources';

/**
 * Build the React Query options for a GET resource.
 *
 * `useGet` and `useGetAll` both go through this, so they share one request
 * pipeline (the `runRequest` they pass is `executeGet`, which applies `handleRes`,
 * `responseType`, header/auth and camelCase handling) and the same `sideEffectGet`
 * cache defaults. Keeping it in one place is what stops the two hooks from
 * drifting — previously `useGetAll` re-implemented the request inline and silently
 * skipped `handleRes` and `sideEffectGet`.
 */
export function buildGetQueryOptions<TReturn, SelectedData>({
  type,
  input,
  runRequest,
  queryConfig
}: {
  type: URLKeysTypes;
  input: unknown;
  runRequest: () => Promise<TReturn>;
  queryConfig: Omit<UseQueryOptions<TReturn, NoddiAsyncError, SelectedData>, 'queryKey' | 'queryFn'>;
}): UseQueryOptions<TReturn, NoddiAsyncError, SelectedData> {
  const { sideEffectGet } = getUrls[type];

  // Spread the caller config first so queryKey/queryFn always win — a caller can't
  // override the shared executeGet pipeline via queryConfig.
  return {
    ...mergeSideEffectGetQueryConfig(sideEffectGet, queryConfig as Record<string, unknown>),
    queryKey: (input !== undefined ? [type, input] : [type]) as [URLKeysTypes, ...unknown[]],
    queryFn: runRequest
  } as UseQueryOptions<TReturn, NoddiAsyncError, SelectedData>;
}
